import map from 'lodash/map'
import React, { CSSProperties, FC } from 'react'

import { Button } from '@Components/Button'
import { ImageV2 } from '@Components/Image'
import { Link } from '@Components/Link'
import { useTranslation } from '@Globals/Translations'

import styles from './PageGrid.module.scss'

type TitlePosition = 'bottom' | 'overlay'

export interface Page {
  brand?: {
    id: string
    logo?: string
    title: string
  }
  description?: string
  id: string
  image: string
  link: string
  pageType: string
  title: string
}

export interface PageGridProps {
  columns?: number
  id?: string
  items: Page[]
  showDescription?: boolean
  titlePosition?: TitlePosition
}

interface PageItemProps extends Page {
  imageWidth: number
  titlePosition: TitlePosition
}

const PageItem: FC<PageItemProps> = ({
  brand,
  description,
  id,
  image,
  imageWidth,
  link,
  pageType,
  title,
  titlePosition,
}) => {
  const { t } = useTranslation()
  const sixtyPercentOfWidth = Math.ceil(imageWidth * 0.6)
  const showOverlay = titlePosition === 'overlay'
  const showBrand = Boolean(brand && brand.logo && !showOverlay)
  const isCollectionPage = pageType === 'collections'

  return (
    <Link
      analytics={{
        dest_page_type: pageType,
        dest_page_type_id: id,
        source_id: 'post_grid_module',
      }}
      className={styles.link}
      href={link}
    >
      <div className={styles.imageSection}>
        <div className={styles.image}>
          {image && (
            <ImageV2
              alt={title}
              layout="fill"
              objectFit="cover"
              objectPosition="center center"
              sizes={`(max-width: 480px) 100vw, (max-width: 768px) 50vw, ${imageWidth}px`}
              src={image}
            />
          )}
        </div>
        {showBrand && (
          <div className={styles.brand}>
            <img
              alt={brand?.title || ''}
              className={styles.image}
              src={brand?.logo as string}
              width={sixtyPercentOfWidth}
            />
          </div>
        )}
        {showOverlay && (
          <div className={styles.overlay}>
            <h4>{title}</h4>
            <Button className={styles.button} color="white" width="auto">
              <span>{t(isCollectionPage ? 'cms:SHOP_NOW' : 'cms:READ_NOW')}</span>
            </Button>
          </div>
        )}
      </div>
      {!showOverlay && (
        <h4 style={{ padding: '10px 5px 0' }}>{title}</h4>
      )}
      {description && (
        <div className={styles.pageItemDescription}>
          {description.length > 120 ? `${description.substring(0, 120)}...` : description}
        </div>
      )}
    </Link>
  )
}
export const PageGrid: FC<PageGridProps> = ({
  columns = 2,
  id,
  items,
  showDescription = false,
  titlePosition = 'overlay',
}) => (
  <div className={styles.pageGrid} id={id} style={{ '--columns': columns } as CSSProperties}>
    {map(items, item => (
      <PageItem
        key={item.id}
        imageWidth={1200 / columns}
        titlePosition={titlePosition}
        {...item}
        description={showDescription ? item.description : undefined}
      />
    ))}
  </div>
)
